import gql from "graphql-tag"

import { MEDIASET_FRAGMENT } from "./mediaSet"
import { MEDIASET_ATTRIBUTES_FRAGMENT } from "graphql/queries/mediaSet"
import { QUIZ_RESPONSE_FRAGMENT } from "graphql/queries/quizResponse"
import { DISPLAYED_PRODUCTS_ATTRIBUTES_FRAGMENT } from "graphql/queries/products"

export const CURRENT_OFFER_FRAGMENT = gql`
  fragment GetCurrentOfferFragment on Offer {
    topBannerText
    discountPercentage
  }
`

export const LEAD_CREATION_USER_FRAGMENT = gql`
  fragment LeadCreationUserFragment on User {
    id
    eCommerceUser
    email
    firstName
    lastName
    fullName
    active
  }
`

export const ACCOUNT_CREATION_USER_FRAGMENT = gql`
  fragment AccountCreationUserFragment on User {
    id
    email
    firstName
    nameOnCard
    fullName
    eCommerceUser
    active
    address {
      id
      streetAddress1
      streetAddress2
      city
      zipCode
      state
    }
    phone {
      id
      phoneAreaCode
      phoneNumber
    }
    profile {
      id
      dob
      gender
      termsAccepted
      hearAboutUs
    }
    stripeCards {
      brand
      last4
      addressZip
    }
    hasEaglePatientId
  }
`

export const PHARMACY_FRAGMENT = gql`
  fragment PharmacyAttributesFragment on Pharmacy {
    id
    name
    address {
      id
      streetAddress1
      streetAddress2
      city
      state
      zipCode
    }
    externalId
    phoneNumber
    faxNumber
    user {
      id
    }
  }
`

export const JOURNEY_PRODUCT_ATTRIBUTES_FRAGMENT = gql`
  fragment JourneyProductAttributesFragment on Product {
    id
    displayedDosage
    daysUntilFirstRefill
    drugType
    drugCategory
    dosageType
    displayedAmount
    displayedDosage
    supplyInDays
    price
    needsPrescription
    variations {
      id
      displayedCadence
      drugType
      dosageType
      dosageValue
      supplyInDays
      userSelectable
    }
  }
`

const JOURNEY_ATTRIBUTES_FRAGMENT = gql`
  fragment JourneyAttributesFragment on Journey {
    id
    kind
    nextActions
    mediaSet {
      ...MediaSetAttributesFragment
    }
    finalProducts {
      id
    }
    userProducts {
      id
      ...JourneyProductAttributesFragment
    }
    doctorProducts {
      id
      ...JourneyProductAttributesFragment
    }
  }
  ${JOURNEY_PRODUCT_ATTRIBUTES_FRAGMENT}
  ${MEDIASET_ATTRIBUTES_FRAGMENT}
`

export const START_JOURNEY_USER_FRAGMENT = gql`
  fragment StartJourneyUserFragment on User {
    id
    activeJourneys {
      ...JourneyAttributesFragment
    }
    journeys {
      ...JourneyAttributesFragment
    }
  }
  ${JOURNEY_ATTRIBUTES_FRAGMENT}
`

export const INSURANCES_USER_FRAGMENT = gql`
  fragment InsurancesUserFragment on User {
    insurances {
      cardholderId
      cardholderFirstName
      cardholderFirstName
      bin
      pcn
      personCode
    }
  }
`

export const CHECKOUT_USER_FRAGMENT = gql`
  fragment CheckoutUserFragment on User {
    id
    email
    nameOnCard
    eCommerceUser
    activeJourneys {
      id
      kind
    }
    address {
      id
      streetAddress1
      streetAddress2
      city
      state
      zipCode
    }
    phone {
      phoneAreaCode
      phoneNumber
    }
    stripeCards {
      brand
      last4
      addressZip
    }
  }
`

export const QUIZ_USER_FRAGMENT = gql`
  fragment ConsultationQuizUserFragment on User {
    id
    activeJourneys {
      id
      kind
      nextActions
      coveQuizResponse {
        id
        ...CoveQuizResponseFragment
      }
    }
  }
  ${QUIZ_RESPONSE_FRAGMENT}
`

export const DOCTOR_MESSAGE_FRAGMENT = gql`
  fragment DoctorMessageFragment on ConsultationMessage {
    id
    body
    authorType
    authorName
    createdAt
    unreadForPatient
  }
`

export const CONSULTATION_FRAGMENT = gql`
  fragment ConsultationFragment on User {
    id
    hasDoctorTreatmentChange
    consultation {
      id
      status
      notAMatch
      unreadDoctorMessagesCount
      activeDoctorSelectedProductsCount
      consultationMessages(newestFirst: true) {
        id
        ...DoctorMessageFragment
      }
      doctor {
        id
        fullName
        medicalSchool
        residency
        qualification
        photoUrl
      }
      doctorMessageDisplayed {
        id
        body
        approvedByUser
        unreadForPatient
        prescriptionStatus
        activeProducts {
          id
          displayedName
          price
        }
      }
    }
  }
  ${DOCTOR_MESSAGE_FRAGMENT}
`

export const SUBSCRIPTIONS_FRAGMENT = gql`
  fragment SubscriptionsFragment on User {
    id
    subscriptions {
      id
      status
      cycleInDays
      nextBillingDate
      estimatedNextBillingDate
      activeProducts {
        id
        drugType
        drugCategory
        dosageType
      }
    }
  }
`

export const ACTIVE_SUBSCRIPTIONS_FRAGMENT = gql`
  fragment ActiveSubscriptionsFragment on User {
    id
    activeSubscriptions {
      id
      status
      nextBillingDate
      estimatedNextBillingDate
      cycleInDays
      activeProducts {
        id
        drugType
        drugCategory
        dosageType
      }
    }
  }
`

export const MIGRAINE_EVENT_FIELDS_FRAGMENT = gql`
  fragment MigraineEventResponseFields on CoveMigraineEvent {
    id
    startedAt
    endedAt
    ongoing
    acuteMedicationTakenTimeframe
    painIntencity
    medicationEffective
    sideEffects
    sideEffectsNotes
    pastTwoDays
    additionalNotes
    user {
      id
      coveMigraineEventsThisMonthCount
      coveMigraineEvents {
        id
      }
    }
  }
`

const GET_PRICING_FRAGMENT = gql`
  fragment GetPricingFragment on Pricing {
    lineItems {
      kind
      product {
        id
      }
      amount
      amountWithDiscount
    }
  }
`

/* external id for pharmacies */
const PHARMACY_ID_CONSTANTS_FRAGMENT = gql`
  fragment GetPharmacyIdConstants on PharmacyIdConstants {
    eagle
    pillpack
  }
`

export const ROOT_QUERY_USER_FRAGMENT = gql`
  fragment RootQueryUserFragment on User {
    id
    email
    firstName
    lastName
    fullName
    active
    isImpersonated
    eCommerceUser
    hasDoctorTreatmentChange
    coveMigraineEventsThisMonthCount
    pharmacy {
      ...PharmacyAttributesFragment
    }
    activeJourneys {
      id
    }
    coveMigraineEvents {
      ...MigraineEventResponseFields
    }
    ...LeadCreationUserFragment
    ...AccountCreationUserFragment
    ...CheckoutUserFragment
    ...StartJourneyUserFragment
    ...ConsultationQuizUserFragment
    ...MediaSetFragment
    ...ConsultationFragment
    ...SubscriptionsFragment
    ...ActiveSubscriptionsFragment
    ...InsurancesUserFragment
  }
  ${LEAD_CREATION_USER_FRAGMENT}
  ${ACCOUNT_CREATION_USER_FRAGMENT}
  ${PHARMACY_FRAGMENT}
  ${CHECKOUT_USER_FRAGMENT}
  ${START_JOURNEY_USER_FRAGMENT}
  ${QUIZ_USER_FRAGMENT}
  ${MEDIASET_FRAGMENT}
  ${CONSULTATION_FRAGMENT}
  ${SUBSCRIPTIONS_FRAGMENT}
  ${ACTIVE_SUBSCRIPTIONS_FRAGMENT}
  ${MIGRAINE_EVENT_FIELDS_FRAGMENT}
  ${INSURANCES_USER_FRAGMENT}
`

/* If you want to add a field to the user - add it into the RootQueryUserFragment or the wrath of the universe will fall upon you */
export const ROOT_QUERY = gql`
  query RootQuery {
    user: currentLead {
      ...RootQueryUserFragment
    }
    currentOffer {
      ...GetCurrentOfferFragment
    }
    displayedProducts {
      ...GetDisplayedProductsFragment
    }
    pricing {
      ...GetPricingFragment
    }
    flipperFlags {
      insuranceUploadEnabled: isEnabled(flag: "cove_insurance_upload_enabled")
      showEditNbdLink: isEnabled(flag: "cove_show_edit_nbd_link")
    }
    pharmacyIdConstants {
      ...GetPharmacyIdConstants
    }
  }
  ${ROOT_QUERY_USER_FRAGMENT}
  ${DISPLAYED_PRODUCTS_ATTRIBUTES_FRAGMENT}
  ${CURRENT_OFFER_FRAGMENT}
  ${GET_PRICING_FRAGMENT}
  ${PHARMACY_ID_CONSTANTS_FRAGMENT}
`
